import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close,
  DeleteOutline,
  Person,
  Phone,
  Email,
  ShoppingCart,
  CalendarToday,
  Edit,
  Print,
  Share,
  LocationOn,
  Home,
  Warehouse,
} from '@mui/icons-material';
import { updateOrderDetails, updateSkuDetails, uploadWarehousePhotos, deleteWarehousePhoto } from '../../services/api';
import { getOrder, getPurchasingManagementOptions } from '../../services/order';
import WarehousePhotoUploadModal from './WarehousePhotoUploadModal';

/**
 * Formats properties_name string by extracting and combining property values
 * @param {string} propertiesName - The raw properties_name string
 * @returns {string} - Formatted properties string
 * 
 * Examples:
 * "0:0: color: white; 1: 0: size: M" → "color: white; size: M"
 * "0:0: Color: Off white; 1: 2: Suitable for height: 80cm" → "Color: Off white; Suitable for height: 80cm"
 * "0:0: Color: 28cm effortless squeezing/reinforcing rod/1 head in total" → "Color: 28cm effortless squeezing/reinforcing rod/1 head in total"
 */
const formatPropertiesName = (propertiesName) => {
  if (!propertiesName || typeof propertiesName !== 'string') {
    return '';
  }

  // Split by semicolon to get individual property parts
  const parts = propertiesName.split(';').map(part => part.trim()).filter(part => part);
  
  const formattedParts = parts.map(part => {
    // Split by colon to extract the property name and value
    const colonParts = part.split(':').map(p => p.trim()).filter(p => p);
    
    // Skip the index parts (0:0:, 1:0:, etc.) and get the actual property
    if (colonParts.length >= 4) {
      // Format: "0:0: color: white" → extract "color: white"
      const propertyName = colonParts[2];
      const propertyValue = colonParts.slice(3).join(':').trim();
      return `${propertyName}: ${propertyValue}`;
    } else if (colonParts.length === 3) {
      // Format: "0:0: Color: 28cm..." → extract "Color: 28cm..."
      const propertyName = colonParts[2];
      return propertyName;
    }
    
    return part; // Return as-is if format is unexpected
  });

  return formattedParts.join('; ');
};

const OrderDetailsModal = ({ open, onClose, orderId }) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skuEditModalOpen, setSkuEditModalOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedSkuIndex, setSelectedSkuIndex] = useState(null);
  const [skuStatus, setSkuStatus] = useState('');
  const [skuQuantity, setSkuQuantity] = useState('');
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [editOrderModalOpen, setEditOrderModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    total_price: '',
    subtotal: '',
    post_fee: '',
    display_shipping_fee: '',
    actual_purchased_price: '',
    additional_fee: '',
    note: '',
    status: '',
    purchasing_platform: '',
    purchasing_account: '',
    goods_type: 'normal'
  });
  const [warehouseUploadModalOpen, setWarehouseUploadModalOpen] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const [purchasingOptions, setPurchasingOptions] = useState({ platforms: [], accounts: [] });
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isZhUi = (i18n?.language || '').toLowerCase().startsWith('zh');

  // Check if order is group buy
  const isGroupBuyOrder = order && order.is_group_buy === true;

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
      fetchPurchasingOptions();
    }
  }, [open, orderId]);

  const fetchPurchasingOptions = async () => {
    try {
      const response = await getPurchasingManagementOptions();
      setPurchasingOptions({
        platforms: Array.isArray(response?.platforms) ? response.platforms : [],
        accounts: Array.isArray(response?.accounts) ? response.accounts : [],
      });
    } catch (err) {
      setPurchasingOptions({ platforms: [], accounts: [] });
    }
  };

  const uniquePlatforms = React.useMemo(() => {
    const values = purchasingOptions.platforms
      .map((item) => (item?.name || '').trim())
      .filter(Boolean);
    return [...new Set(values)];
  }, [purchasingOptions.platforms]);

  const accountsForSelectedPlatform = React.useMemo(() => {
    const selectedPlatform = (editFormData.purchasing_platform || '').trim();
    const source = purchasingOptions.accounts || [];
    const filtered = selectedPlatform
      ? source.filter((item) => (item?.platform_name || '').trim() === selectedPlatform)
      : source;
    return filtered
      .map((item) => (item?.purchasing_account || '').trim())
      .filter(Boolean);
  }, [purchasingOptions.accounts, editFormData.purchasing_platform]);

  const uniqueAccounts = React.useMemo(() => {
    const values = accountsForSelectedPlatform
      .filter(Boolean);
    return [...new Set(values)];
  }, [accountsForSelectedPlatform]);

  useEffect(() => {
    // If platform is already selected and account is empty, auto-pick first valid account.
    // This prevents the edit modal from showing blank account after options load asynchronously.
    if (!editOrderModalOpen) return;
    if (!editFormData.purchasing_platform) return;
    if (editFormData.purchasing_account) return;
    if (!uniqueAccounts.length) return;

    setEditFormData((prev) => ({
      ...prev,
      purchasing_account: uniqueAccounts[0],
    }));
  }, [
    editOrderModalOpen,
    editFormData.purchasing_platform,
    editFormData.purchasing_account,
    uniqueAccounts,
  ]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getOrder(orderId);
      setOrder(response);
    } catch (err) {
      setError(err.message || t('orders:messages.failedToFetch'));
    } finally {
      setLoading(false);
    }
  };

  const getDisplayOrderTitle = () => {
    if (!order) return '';
    if (isZhUi) return order.chinese_title || order.title;
    return order.title;
  };

  const getDisplaySkuPropertiesName = (skuItem) => {
    if (!skuItem) return '';
    if (!isZhUi) return skuItem.properties_name;

    const chineseSku = order?.chinese_sku;
    const targetSkuId = String(skuItem.sku_id);
    let matched = null;

    if (Array.isArray(chineseSku)) {
      matched = chineseSku.find((entry) => String(entry?.sku_id) === targetSkuId);
    } else if (chineseSku && Array.isArray(chineseSku.sku)) {
      matched = chineseSku.sku.find((entry) => String(entry?.sku_id) === targetSkuId);
    } else if (chineseSku && typeof chineseSku === 'object') {
      matched = chineseSku[targetSkuId] || chineseSku[skuItem.sku_id];
    }

    if (!matched) return skuItem.properties_name;
    if (typeof matched === 'string') return matched;
    return matched.properties_name || skuItem.properties_name;
  };

  const displaySkuList = React.useMemo(() => {
    return Array.isArray(order?.sku) ? order.sku : [];
  }, [order]);

  const getStatusString = (status) => {
    switch (status) {
      case 1: return t('orders:status.unpaid');
      case 2: return t('orders:status.processing');
      case 3: return t('orders:status.purchased');
      case 4: return t('orders:status.inWarehouse');
      case 5: return t('orders:status.shippedOut');
      case 6: return t('orders:status.inNigeria');
      case 7: return t('orders:status.received');
      case 8: return t('orders:status.refunded');
      case 9: return t('orders:status.success');
      case 10: return t('orders:status.closed');
      default: return t('orders:status.unknown');
    }
  };

  const getStatusColor = (status) => {
    const statusStr = typeof status === 'number' ? getStatusString(status) : status;
    switch (statusStr) {
      case 'Success': return '#4CAF50';
      case 'Processing': return '#FF9800';
      case 'Unpaid': return '#9E9E9E';
      case 'Refunded': return '#F44336';
      case 'Closed': return '#757575';
      case 'In Warehouse': return '#2196F3';
      case 'Shipped Out': return '#9C27B0';
      case 'In Nigeria': return '#FF5722';
      case 'Receieved': return '#4CAF50';
      default: return '#2196F3';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Order ${order?.order_number}`,
        text: `Order details for ${order?.order_number}`,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`Order ${order?.order_number} - ${getDisplayOrderTitle()}`);
    }
  };

  const handleEditSku = (skuItem, index) => {
    setSelectedSku(skuItem);
    setSelectedSkuIndex(index);
    setSkuStatus(skuItem.status.toString());
    setSkuQuantity(skuItem.quantity.toString());
    setSkuEditModalOpen(true);
  };

  const handleCloseSkuEditModal = () => {
    setSkuEditModalOpen(false);
    setSelectedSku(null);
    setSelectedSkuIndex(null);
    setSkuStatus('');
    setSkuQuantity('');
  };

  const handleSaveSkuEdit = async () => {
    if (!selectedSku || selectedSkuIndex === null) return;
    
    setLoading(true);
    try {
      const updateData = {
        status: parseInt(skuStatus),
        quantity: parseInt(skuQuantity)
      };
      
      // Call API to update SKU
      const response = await updateSkuDetails(order.id, selectedSku.id, updateData);
      
      if (response.success) {
        // Update the SKU in the local order state
        const updatedOrder = { ...order };
        updatedOrder.sku[selectedSkuIndex] = {
          ...selectedSku,
          status: parseInt(skuStatus),
          quantity: parseInt(skuQuantity),
          total_price: response.data.total_price
        };
        
        setOrder(updatedOrder);
        handleCloseSkuEditModal();
        setSnackbar({
          open: true,
          message: t('orders:messages.skuUpdatedSuccess'),
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || t('orders:messages.skuUpdateFailed'),
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating SKU:', error);
      setSnackbar({
        open: true,
        message: t('orders:messages.skuUpdateFailedRetry'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setEnlargedImage(imageUrl);
  };

  const handleCloseEnlargedImage = () => {
    setEnlargedImage(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleUploadWarehousePhotos = async (formData) => {
    if (!order) return;
    
    setUploadingPhotos(true);
    try {
      const response = await uploadWarehousePhotos(order.id, formData);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || t('orders:messages.photosUploadedSuccess'),
          severity: 'success'
        });
        
        // Reload order data to get updated warehouse photos
        await fetchOrderDetails();
        
        // Close the upload modal
        setWarehouseUploadModalOpen(false);
      } else {
        setSnackbar({
          open: true,
          message: response.message || t('orders:messages.photosUploadFailed'),
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error uploading warehouse photos:', error);
      setSnackbar({
        open: true,
        message: t('orders:messages.photosUploadError'),
        severity: 'error'
      });
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeleteWarehousePhoto = async (photoId) => {
    if (!order || !photoId) return;
    setDeletingPhotoId(photoId);
    try {
      const response = await deleteWarehousePhoto(order.id, photoId);
      if (response.success) {
        setOrder((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            warehouse_photos: (prev.warehouse_photos || []).filter((photo) => photo.id !== photoId)
          };
        });
        setSnackbar({
          open: true,
          message: response.message || 'Warehouse image deleted successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to delete warehouse image',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Failed to delete warehouse image',
        severity: 'error'
      });
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const handleEditOrder = () => {
    if (!order) return;
    
    // Initialize form data with current order values
    setEditFormData({
      total_price: order.total_price || '',
      subtotal: order.subtotal || '',
      post_fee: order.post_fee || '',
      display_shipping_fee: order.display_shipping_fee || '',
      actual_purchased_price: order.actual_purchased_price || '',
      additional_fee: order.additional_fee || '',
      note: order.note || '',
      status: order.status?.toString() || '',
      purchasing_platform: order.purchasing_platform || '',
      purchasing_account: order.purchasing_account || '',
      goods_type: order.goods_type || 'normal'
    });
    setEditOrderModalOpen(true);
  };

  const handleCloseEditOrderModal = () => {
    setEditOrderModalOpen(false);
    setEditFormData({
      total_price: '',
      subtotal: '',
      post_fee: '',
      display_shipping_fee: '',
      actual_purchased_price: '',
      additional_fee: '',
      note: '',
      status: '',
      purchasing_platform: '',
      purchasing_account: '',
      goods_type: 'normal'
    });
  };

  const handleEditFormChange = (field, value) => {
    if (field === 'purchasing_platform') {
      setEditFormData(prev => ({
        ...prev,
        purchasing_platform: value,
        purchasing_account: '',
      }));
      return;
    }
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveOrderEdit = async () => {
    if (!order) return;
    
    // Validate that note is provided when additional_fee > 0
    const additionalFee = parseFloat(editFormData.additional_fee) || 0;
    if (additionalFee > 0 && (!editFormData.note || editFormData.note.trim() === '')) {
      setSnackbar({
        open: true,
        message: 'Please narrate what the additional fee is for in the Note field before saving.',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    try {
      // Prepare update data
      const updateData = {
        total_price: editFormData.total_price,
        subtotal: editFormData.subtotal,
        post_fee: editFormData.post_fee,
        display_shipping_fee: editFormData.display_shipping_fee,
        actual_purchased_price: editFormData.actual_purchased_price,
        additional_fee: editFormData.additional_fee,
        note: editFormData.note,
        status: parseInt(editFormData.status),
        purchasing_platform: editFormData.purchasing_platform,
        purchasing_account: editFormData.purchasing_account,
        goods_type: editFormData.goods_type
      };
      
      // Call API to update order
      const response = await updateOrderDetails(order.id, updateData);
      
      if (response.success) {
        const serverData = response.data || {};
        // Update the order with new values
        const updatedOrder = {
          ...order,
          ...updateData,
          total_price: serverData.total_price ?? updateData.total_price,
          inclusive_total_price: serverData.inclusive_total_price ?? order.inclusive_total_price,
          post_fee: serverData.post_fee ?? updateData.post_fee,
          display_shipping_fee: serverData.display_shipping_fee ?? updateData.display_shipping_fee
        };

        // If order status was updated, sync SKU statuses to match
        if (updateData.status !== undefined && updateData.status !== order.status) {
          updatedOrder.sku = updatedOrder.sku.map(sku => ({
            ...sku,
            status: updateData.status
          }));
        }

        setOrder(updatedOrder);
        handleCloseEditOrderModal();
        setSnackbar({
          open: true,
          message: t('orders:messages.orderUpdatedSuccess'),
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || t('orders:messages.orderUpdateFailed'),
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating order:', error);
      setSnackbar({
        open: true,
        message: t('orders:messages.orderUpdateFailedRetry'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateTimelineEntries = (order) => {
    const timeline = [];
    
    // Always show created_at
    timeline.push({
      date: order.created_at,
      status: t('orders:details.orderCreated'),
      description: t('orders:details.orderWasPlaced'),
      color: 'primary',
      variant: 'outlined'
    });

    // Show expired_at if available
    if (order.expired_at) {
      timeline.push({
        date: order.expired_at,
        status: t('orders:details.orderExpired'),
        description: t('orders:details.orderHasExpired'),
        color: 'error',
        variant: 'filled'
      });
    }

    // Show paid_at if available
    if (order.paid_at) {
      timeline.push({
        date: order.paid_at,
        status: t('orders:details.paymentCompleted'),
        description: t('orders:details.paymentWasProcessed'),
        color: 'success',
        variant: 'filled'
      });
    }

    // Show send_at if available
    if (order.send_at) {
      timeline.push({
        date: order.send_at,
        status: t('orders:details.orderShipped'),
        description: t('orders:details.orderHasBeenShipped'),
        color: 'info',
        variant: 'filled'
      });
    }

    // Show received_at if available
    if (order.received_at) {
      timeline.push({
        date: order.received_at,
        status: t('orders:details.orderReceived'),
        description: t('orders:details.orderHasBeenReceived'),
        color: 'success',
        variant: 'filled'
      });
    }

    // Show refunded_at if available
    if (order.refunded_at) {
      timeline.push({
        date: order.refunded_at,
        status: t('orders:details.orderRefunded'),
        description: t('orders:details.orderHasBeenRefunded'),
        color: 'error',
        variant: 'filled'
      });
    }

    // Always show updated_at (but only if different from created_at)
    if (order.updated_at && order.updated_at !== order.created_at) {
      timeline.push({
        date: order.updated_at,
        status: t('orders:details.lastUpdated'),
        description: t('orders:details.orderInfoWasUpdated'),
        color: 'default',
        variant: 'outlined'
      });
    }

    // Sort timeline by date
    return timeline.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingCart color="primary" />
          <Typography variant="h6" component="div">
            {t('orders:details.title')}
          </Typography>
          {order && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={getStatusString(order.status)}
                sx={{
                  backgroundColor: getStatusColor(order.status),
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
              {isGroupBuyOrder && (
                <Chip
                  label= {t('orders:details.groupBuyOrder')}
                  sx={{
                    backgroundColor: '#9c27b0',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                  size="small"
                />
              )}
            </Box>
          )}
        </Box>
        <Box>
          <Tooltip title={t('orders:tooltips.print')}>
            <IconButton onClick={handlePrint} size="small">
              <Print />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('orders:tooltips.share')}>
            <IconButton onClick={handleShare} size="small">
              <Share />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {order && !loading && (
          <Box sx={{ p: 3 }}>
            {order.is_demo && (
              <Alert severity="warning" variant="filled" sx={{ mb: 3, fontWeight: 'bold' }}>
                {t('orders:details.staffDemoWarning')}
              </Alert>
            )}
            {/* Order Header */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="h5" gutterBottom>
                    {getDisplayOrderTitle()}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {t('orders:details.orderNumber')} #{order.order_number}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {t('orders:details.warehouseNumber')} {order.warehouse_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('orders:details.created')} {formatDate(order.created_at)}
                  </Typography>
                  {order.updated_at !== order.created_at && (
                    <Typography variant="body2" color="text.secondary">
                      {t('orders:details.lastUpdatedLabel')} {formatDate(order.updated_at)}
                    </Typography>
                  )}
                  {order?.user?.bin_code !== order.created_at && (
                    <Typography variant="body2" color="text.secondary">
                      {t('orders:details.binCode')} {order?.user?.bin_code || '-'}
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {formatCurrency(order.inclusive_total_price || order.total_price)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('orders:details.totalAmount')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person color="primary" />
                      {t('orders:details.customerInformation')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {order.user?.username?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {order.user?.username || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.customerId')} {order.user?.id || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">
                        {order.user?.phone || 'N/A'}
                      </Typography>
                    </Box>
                    {order.user?.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">
                          {order.user.email}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Receiver Address Information */}
              {order.receiver_address && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn color="primary" />
                        {t('orders:details.receiverAddress')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <Home />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {order.receiver_address.receiver_name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('orders:details.receiver')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">
                          {order.receiver_address.receiver_phone || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                        <Box>
                          <Typography variant="body2">
                            {order.receiver_address.address || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.receiver_address.city}, {order.receiver_address.state}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Seller Information */}
              {order.seller_info && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person color="primary" />
                        {t('orders:details.sellerInformation')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'success.main' }}>
                          {order.seller_info.shop_name?.charAt(0)?.toUpperCase() || 'S'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {order.seller_info.shop_name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.seller_info.title || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.sellerId')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {order.seller_info.user_num_id || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('orders:details.itemUrl')}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'primary.main', 
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(order.detail_url, '_blank')}
                          >
                            {t('orders:details.visitItemPage')}
                          </Typography>
                        </Box>
                      {order.seller_info.zhuy && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('orders:details.storeUrl')}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'primary.main', 
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(order.seller_info.zhuy, '_blank')}
                          >
                            {t('orders:details.visitStore')}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {/*No seller infor still show item url*/}
              {!order.seller_info && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person color="primary" />
                        {t('orders:details.sellerInformation')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('orders:details.itemUrl')}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'primary.main', 
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(order.detail_url, '_blank')}
                          >
                            {t('orders:details.visitItemPage')}
                          </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {/* Order Information */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShoppingCart color="primary" />
                      {t('orders:details.orderInformation')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.quantity')}
                        </Typography>
                        <Typography variant="h6">
                          {order.total_quantity || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.unitPrice')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.price)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.inclusivePrice')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.inclusive_price)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.subtotal')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.subtotal)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.inclusiveSubtotal')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.inclusive_subtotal)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.exchangeRate')}
                        </Typography>
                        <Typography variant="h6">
                          {order.rate || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.status')}
                        </Typography>
                        <Chip
                          label={getStatusString(order.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(order.status),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.serviceFee')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.service_fee)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.postFee')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.post_fee)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.displayShippingFee')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.display_shipping_fee)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.discount')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.discount_price)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.inclusiveDiscount')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.inclusive_discount_price)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.purchasedPrice')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.actual_purchased_price)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.additionalFee')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.additional_fee)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.bonusUsedAmount')}
                        </Typography>
                        <Typography variant="h6">
                          ₦ {order.bonus_used_amount || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.totalPrice')}
                        </Typography>
                        <Typography variant="h6">
                          
                          {formatCurrency(order.total_price)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.inclusiveTotalPrice')}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(order.inclusive_total_price)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.serviceFeePercentage')}
                        </Typography>
                        <Typography variant="h6">
                           {order.service_fee_percentage }%
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Platform & Purchasing Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Edit color="primary" />
                      {t('orders:details.platformPurchasingInfo')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.platform')}
                        </Typography>
                        <Typography variant="h6">
                          {order.platform || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.purchasingPlatform')}
                        </Typography>
                        <Typography variant="h6">
                          {order.purchasing_platform || t('orders:details.notSpecified')}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.purchasingAccount')}
                        </Typography>
                        <Typography variant="h6">
                          {order.purchasing_account || t('orders:details.notSpecified')}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.remark')}
                        </Typography>
                        <Typography variant="h6">
                          {order.remark || t('orders:details.none')}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('orders:details.note')}
                        </Typography>
                        <Typography variant="h6">
                          {order.note || t('orders:details.noAdditionalNotes')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* SKU Information */}
              {displaySkuList.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingCart color="primary" />
                        {t('orders:details.productDetails')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:details.item')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:details.skuOrderNumber')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:details.quantity')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:table.price')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:details.discount')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:details.serviceFee')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:table.total')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:details.status')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('orders:table.actions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {displaySkuList.map((skuItem, index) => (
                              <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {skuItem.url && (
                                      <img
                                        src={skuItem.url}
                                        alt="Product"
                                        referrerPolicy="no-referrer"
                                        style={{
                                          width: 40,
                                          height: 40,
                                          objectFit: 'cover',
                                          borderRadius: 4,
                                          cursor: 'pointer',
                                          transition: 'transform 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.transform = 'scale(1)';
                                        }}
                                        onClick={() => handleImageClick(skuItem.url)}
                                      />
                                    )}
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {formatPropertiesName(getDisplaySkuPropertiesName(skuItem)) || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {skuItem.sku_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {skuItem.quantity}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(skuItem.price)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(skuItem.discount_price || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(skuItem.service_fee)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(skuItem.total_price)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={getStatusString(skuItem.status)}
                                    size="small"
                                    sx={{
                                      backgroundColor: getStatusColor(skuItem.status),
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    disabled={isGroupBuyOrder}
                                    sx={{
                                      backgroundColor: isGroupBuyOrder ? '#cccccc' : '#4caf50',
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '0.75rem',
                                      textTransform: 'none',
                                      '&:hover': {
                                        backgroundColor: isGroupBuyOrder ? '#cccccc' : '#45a049'
                                      },
                                      '&.Mui-disabled': {
                                        backgroundColor: '#cccccc',
                                        color: '#666666'
                                      }
                                    }}
                                    onClick={() => handleEditSku(skuItem, index)}
                                    title={isGroupBuyOrder ? t('orders:tooltips.groupBuyOrderLocked') : ''}
                                  >
                                    {t('orders:modals.updatingSku.title')}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Summary Section */}
                      <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('orders:details.totalType')} {order.sku.length}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('orders:details.totalQuantity')} {order.total_quantity || order.sku.reduce((sum, item) => sum + item.quantity, 0)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('orders:details.totalPriceLabel')} {formatCurrency(order.subtotal)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('orders:details.serviceFeeLabel')} {formatCurrency(order.service_fee)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('orders:details.localShippingFee')} {formatCurrency(order.post_fee)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {t('orders:details.totalAmountLabel')} {formatCurrency(order.total_price)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Product Image */}
              {order.pic_url && (
                <Grid size={{ xs: 12 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('orders:details.productImage')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ textAlign: 'center' }}>
                        <img
                          src={order.pic_url}
                          alt="Product"
                          referrerPolicy="no-referrer"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Warehouse Item Images */}
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Warehouse color="primary" />
                      {t('orders:details.warehouseItemImages')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {order.warehouse_photos && order.warehouse_photos.length > 0 ? (
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                        gap: 2,
                        p: 2,
                        backgroundColor: '#f9f9f9',
                        borderRadius: 1
                      }}>
                        {order.warehouse_photos.map((photo, index) => {
                          return (
                          <Box key={photo.id} sx={{ 
                            position: 'relative',
                            aspectRatio: '1',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '2px solid #4caf50',
                            backgroundColor: '#fff',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: '#2e7d32',
                              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                              transform: 'scale(1.02)'
                            }
                          }}
                          onClick={() => handleImageClick(photo.image_url || photo.image)}
                          >
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteWarehousePhoto(photo.id);
                              }}
                              disabled={deletingPhotoId === photo.id}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                color: '#fff',
                                '&:hover': {
                                  backgroundColor: 'rgba(211, 47, 47, 0.85)'
                                },
                                '&.Mui-disabled': {
                                  color: '#fff',
                                  opacity: 0.75
                                }
                              }}
                            >
                              {deletingPhotoId === photo.id ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <DeleteOutline fontSize="small" />
                              )}
                            </IconButton>
                            <img
                              src={photo.image_url || photo.image}
                              alt={`Warehouse photo ${index + 1}`}
                              referrerPolicy="no-referrer"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                              }}
                            />
                            <Box sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              p: 1,
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption">
                                {new Date(photo.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                        gap: 2,
                        p: 2,
                        backgroundColor: '#f9f9f9',
                        borderRadius: 1
                      }}>
                        {/* Placeholder when no images */}
                        <Box sx={{ 
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '2px dashed #ccc',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fff',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#1976d2',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
                          }
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 1,
                            p: 2
                          }}>
                            <Box sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              backgroundColor: '#e3f2fd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1
                            }}>
                              <Typography variant="h6" color="primary">
                                📦
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                              {t('orders:details.noWarehouseImages')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" textAlign="center">
                              {t('orders:details.uploadImagesUsingButton')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Info message */}
                    <Box sx={{ 
                      mt: 2, 
                      p: 2, 
                      backgroundColor: '#e8f5e8', 
                      borderRadius: 1,
                      border: '1px solid #c8e6c9'
                    }}>
                      <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ fontSize: '1.2rem' }}>ℹ️</Box>
                        {order.warehouse_photos && order.warehouse_photos.length > 0 
                          ? t('orders:details.showingImages', { 
                              count: order.warehouse_photos.length,
                              plural: order.warehouse_photos.length !== 1 ? 's' : ''
                            })
                          : t('orders:details.uploadWarehouseImagesInfo')
                        }
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Order Timeline */}
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday color="primary" />
                      {t('orders:details.orderTimeline')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('orders:details.date')}</TableCell>
                            <TableCell>{t('orders:details.status')}</TableCell>
                            <TableCell>{t('orders:details.description')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {generateTimelineEntries(order).map((entry, index) => (
                            <TableRow key={index}>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={entry.status}
                                  size="small"
                                  color={entry.color}
                                  variant={entry.variant}
                                />
                              </TableCell>
                              <TableCell>{entry.description}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} variant="outlined">
          {t('orders:modals.close')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Warehouse />}
          onClick={() => setWarehouseUploadModalOpen(true)}
          disabled={isGroupBuyOrder}
          sx={{
            backgroundColor: isGroupBuyOrder ? '#cccccc' : '#ff9800',
            color: 'white',
            '&:hover': {
              backgroundColor: isGroupBuyOrder ? '#cccccc' : '#f57c00'
            },
            '&.Mui-disabled': {
              backgroundColor: '#cccccc',
              color: '#666666'
            }
          }}
          title={isGroupBuyOrder ? t('orders:tooltips.groupBuyOrderLocked') : ''}
        >
          {t('orders:modals.uploadImages')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={handleEditOrder}
          disabled={isGroupBuyOrder}
          sx={{
            backgroundColor: isGroupBuyOrder ? '#cccccc' : '#1976d2',
            color: 'white',
            '&:hover': {
              backgroundColor: isGroupBuyOrder ? '#cccccc' : '#1565c0'
            },
            '&.Mui-disabled': {
              backgroundColor: '#cccccc',
              color: '#666666'
            }
          }}
          title={isGroupBuyOrder ? t('orders:tooltips.groupBuyOrderLocked') : ''}
        >
          {t('orders:modals.updateOrder')}
        </Button>
      </DialogActions>

      {/* SKU Edit Modal */}
      <Dialog 
        open={skuEditModalOpen} 
        onClose={handleCloseSkuEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 'bold'
        }}>
          {t('orders:modals.updatingSku.title')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            backgroundColor: 'white', 
            p: 3, 
            borderRadius: 1,
            border: '1px solid #e0e0e0'
          }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="status-label" sx={{ fontWeight: 'bold', color: '#333' }}>
                {t('orders:modals.updatingSku.status')}
              </InputLabel>
              <Select
                labelId="status-label"
                value={skuStatus}
                onChange={(e) => setSkuStatus(e.target.value)}
                label={t('orders:modals.updatingSku.status')}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  }
                }}
              >
                <MenuItem value="1">{t('orders:status.unpaid')}</MenuItem>
                <MenuItem value="2">{t('orders:status.processing')}</MenuItem>
                <MenuItem value="3">{t('orders:status.purchased')}</MenuItem>
                <MenuItem value="4">{t('orders:status.inWarehouse')}</MenuItem>
                <MenuItem value="5">{t('orders:status.shippedOut')}</MenuItem>
                <MenuItem value="6">{t('orders:status.inNigeria')}</MenuItem>
                <MenuItem value="7">{t('orders:status.received')}</MenuItem>
                <MenuItem value="8">{t('orders:status.refunded')}</MenuItem>
                <MenuItem value="9">{t('orders:status.success')}</MenuItem>
                <MenuItem value="10">{t('orders:status.closed')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <TextField
                label={t('orders:modals.updatingSku.quantity')}
                type="number"
                value={skuQuantity}
                onChange={(e) => setSkuQuantity(e.target.value)}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontWeight: 'bold',
                    color: '#333'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  }
                }}
                inputProps={{
                  min: 1,
                  step: 1
                }}
              />
            </FormControl>

            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveSkuEdit}
              disabled={loading}
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontWeight: 'bold',
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              }}
            >
              {loading ? t('orders:modals.updatingSku.saving') : t('orders:modals.updatingSku.save')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Order Modal */}
      <Dialog 
        open={editOrderModalOpen} 
        onClose={handleCloseEditOrderModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 'bold'
        }}>
          {t('orders:modals.editOrder.title')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            backgroundColor: 'white', 
            p: 3, 
            borderRadius: 1,
            border: '1px solid #e0e0e0'
          }}>
            <Grid container spacing={3}>
              {/* Total Price */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('orders:modals.editOrder.totalPrice')}
                  type="number"
                  value={editFormData.total_price}
                  onChange={(e) => handleEditFormChange('total_price', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                  inputProps={{
                    step: 0.01,
                    min: 0
                  }}
                />
              </Grid>

              {/* Subtotal */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('orders:modals.editOrder.subtotal')}
                  type="number"
                  value={editFormData.subtotal}
                  onChange={(e) => handleEditFormChange('subtotal', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                  inputProps={{
                    step: 0.01,
                    min: 0
                  }}
                />
              </Grid>

              {/* Post Fee */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('orders:modals.editOrder.postFee')}
                  type="number"
                  value={editFormData.post_fee}
                  onChange={(e) => handleEditFormChange('post_fee', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                  inputProps={{
                    step: 0.01,
                    min: 0
                  }}
                />
              </Grid>

             
              {/* Actual Purchased Price */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('orders:modals.editOrder.actualPurchasedPrice')}
                  type="number"
                  value={editFormData.actual_purchased_price}
                  onChange={(e) => handleEditFormChange('actual_purchased_price', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                  inputProps={{
                    step: 0.01,
                    min: 0
                  }}
                />
              </Grid>

              {/* Additional Fee */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('orders:modals.editOrder.additionalFee')}
                  type="number"
                  value={editFormData.additional_fee}
                  onChange={(e) => handleEditFormChange('additional_fee', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                  inputProps={{
                    step: 0.01,
                    min: 0
                  }}
                />
              </Grid>
             

              {/* Status */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel 
                    id="status-label" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'unset',
                      maxWidth: 'none'
                    }}
                  >
                    {t('orders:modals.editOrder.status')}
                  </InputLabel>
                  <Select
                    labelId="status-label"
                    value={editFormData.status}
                    onChange={(e) => handleEditFormChange('status', e.target.value)}
                    label={t('orders:modals.editOrder.status')}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0'
                      }
                    }}
                  >
                    <MenuItem value="1">{t('orders:status.unpaid')}</MenuItem>
                    <MenuItem value="2">{t('orders:status.processing')}</MenuItem>
                    <MenuItem value="3">{t('orders:status.purchased')}</MenuItem>
                    <MenuItem value="4">{t('orders:status.inWarehouse')}</MenuItem>
                    <MenuItem value="5">{t('orders:status.shippedOut')}</MenuItem>
                    <MenuItem value="6">{t('orders:status.inNigeria')}</MenuItem>
                    <MenuItem value="7">{t('orders:status.received')}</MenuItem>
                    <MenuItem value="8">{t('orders:status.refunded')}</MenuItem>
                    <MenuItem value="9">{t('orders:status.success')}</MenuItem>
                    <MenuItem value="10">{t('orders:status.closed')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              

              {/* Platform */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel 
                    id="platform-label" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'unset',
                      maxWidth: 'none'
                    }}
                  >
                    {t('orders:modals.editOrder.platform')}
                  </InputLabel>
                  <Select
                    labelId="platform-label"
                    value={editFormData.purchasing_platform}
                    onChange={(e) => handleEditFormChange('purchasing_platform', e.target.value)}
                    label={t('orders:modals.editOrder.platform')}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0'
                      }
                    }}
                  >
                    <MenuItem value="">{t('orders:details.notSpecified')}</MenuItem>
                    {uniquePlatforms.map((platform) => (
                      <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                    ))}
                    {editFormData.purchasing_platform &&
                    !uniquePlatforms.includes(editFormData.purchasing_platform) ? (
                      <MenuItem value={editFormData.purchasing_platform}>
                        {editFormData.purchasing_platform}
                      </MenuItem>
                    ) : null}
                  </Select>
                </FormControl>
              </Grid>

              {/* Account */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel 
                    id="account-label" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'unset',
                      maxWidth: 'none'
                    }}
                  >
                    {t('orders:modals.editOrder.account')}
                  </InputLabel>
                  <Select
                    labelId="account-label"
                    value={editFormData.purchasing_account}
                    onChange={(e) => handleEditFormChange('purchasing_account', e.target.value)}
                    label={t('orders:modals.editOrder.account')}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0'
                      }
                    }}
                  >
                    <MenuItem value="">{t('orders:details.notSpecified')}</MenuItem>
                    {uniqueAccounts.map((account) => (
                      <MenuItem key={account} value={account}>{account}</MenuItem>
                    ))}
                    {editFormData.purchasing_account &&
                    !uniqueAccounts.includes(editFormData.purchasing_account) ? (
                      <MenuItem value={editFormData.purchasing_account}>
                        {editFormData.purchasing_account}
                      </MenuItem>
                    ) : null}
                  </Select>
                </FormControl>
              </Grid>
             

              {/* Note */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={parseFloat(editFormData.additional_fee) > 0 ? t('orders:modals.editOrder.noteRequired') : t('orders:modals.editOrder.note')}
                  multiline
                  rows={3}
                  value={editFormData.note}
                  onChange={(e) => handleEditFormChange('note', e.target.value)}
                  fullWidth
                  required={parseFloat(editFormData.additional_fee) > 0}
                  error={parseFloat(editFormData.additional_fee) > 0 && (!editFormData.note || editFormData.note.trim() === '')}
                  helperText={parseFloat(editFormData.additional_fee) > 0 ? t('orders:modals.editOrder.noteHelper') : ''}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                />
              </Grid>

              {/* Mark as Sensitive Goods */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.goods_type === 'special'}
                      onChange={(e) => handleEditFormChange('goods_type', e.target.checked ? 'special' : 'normal')}
                      sx={{
                        color: '#D32F2F',
                        '&.Mui-checked': {
                          color: '#D32F2F',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                        {t('orders:modals.editOrder.markAsSpecial')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {t('orders:modals.editOrder.specialGoodsDescription')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCloseEditOrderModal}
                sx={{
                  borderColor: '#e0e0e0',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#ccc'
                  }
                }}
              >
                {t('orders:modals.editOrder.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveOrderEdit}
                disabled={loading}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#45a049'
                  }
                }}
              >
                {loading ? t('orders:modals.editOrder.saving') : t('orders:modals.editOrder.saveChanges')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <Dialog
          open={!!enlargedImage}
          onClose={handleCloseEnlargedImage}
          maxWidth={false}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              maxWidth: '90vw',
              maxHeight: '90vh'
            }
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              cursor: 'pointer'
            }}
            onClick={handleCloseEnlargedImage}
          >
            <img
              src={enlargedImage}
              alt="Product Enlarged"
              referrerPolicy="no-referrer"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 8,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                cursor: 'default'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </Dialog>
      )}

      {/* Warehouse Photo Upload Modal */}
      <WarehousePhotoUploadModal
        open={warehouseUploadModalOpen}
        onClose={() => setWarehouseUploadModalOpen(false)}
        onUpload={handleUploadWarehousePhotos}
        orderNumber={order?.order_number}
        loading={uploadingPhotos}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default OrderDetailsModal;
